import { useEffect, useRef } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getPrint, type PrintLabel, type PrintReceipt, type PrintSummary } from './api'
import { dollars } from './member'
import { labelLines } from './quickActions'
import './print.css'

const money = (s: string): string => dollars(Math.round(Number(s) * 100))
const termText = (m: number | null): string => (m ? `${m} month${m === 1 ? '' : 's'}` : '')

function Receipt({ d }: { d: PrintReceipt }) {
  return (
    <article className="print-sheet print-letter mx-auto max-w-[8.5in] p-10 font-serif text-[12pt] leading-relaxed">
      <h1 className="m-0 text-[22pt]">Receipt</h1>
      <p className="mt-1 text-[10pt]">Order {d.order_key}{d.date ? ` · ${d.date}` : ''}{d.method ? ` · ${d.method}` : ''}{d.last4 ? ` ending ${d.last4}` : ''}</p>
      {(d.details ?? []).length > 0 && <p className="mt-0 text-[10pt]">{d.details.join(' · ')}</p>}
      <p className="mt-6">{[d.payer_name, ...d.address].map((l, i) => <span key={i} className="block">{l}</span>)}</p>
      <table className="mt-6 w-full border-collapse text-left">
        <thead><tr className="border-b border-black"><th className="py-1">Item</th><th>Term</th><th>For</th><th className="text-right">Amount</th></tr></thead>
        <tbody>
          {d.lines.map((l, i) => (
            <tr key={i} className="border-b border-black/20"><td className="py-1">{l.title}</td><td>{termText(l.term_months)}</td><td>{l.for_whom ?? 'Yourself'}</td><td className="text-right">{money(l.amount)}</td></tr>
          ))}
        </tbody>
        <tfoot><tr><td colSpan={3} className="pt-2 text-right font-bold">Total paid</td><td className="pt-2 text-right font-bold">{money(d.total)}</td></tr></tfoot>
      </table>
      <p className="mt-8 text-[10pt]">Thank you for your membership.</p>
    </article>
  )
}

function Label({ d }: { d: PrintLabel }) {
  return (
    <div className="print-label-wrap">
      <div className="print-sheet print-label border border-dashed border-black/40 font-sans" aria-label="Address label">
        {labelLines(d).map((l, i) => <div key={i} className={i === 0 ? 'font-bold' : ''}>{l}</div>)}
      </div>
    </div>
  )
}

function Summary({ d }: { d: PrintSummary }) {
  return (
    <article className="print-sheet mx-auto max-w-[8.5in] p-8 font-sans text-[10.5pt] leading-snug">
      <h1 className="m-0 text-[20pt]">{d.name}{d.account_number ? <span className="ml-3 text-[12pt] font-normal">#{d.account_number}</span> : null}</h1>
      {d.company && <div>{d.company}</div>}
      <div className="mt-1">{d.address.join(', ')}</div>
      <div>{[d.email, d.phone].filter(Boolean).join(' · ')}</div>
      <h2 className="mb-1 mt-5 text-[11pt] uppercase tracking-wide">Titles</h2>
      <table className="w-full border-collapse text-left">
        <tbody>{d.titles.map((t) => <tr key={t.name} className="border-b border-black/20"><td className="py-1">{t.name}</td><td>{t.state}</td><td>{t.end_date ? `ends ${t.end_date}` : ''}</td></tr>)}</tbody>
      </table>
      <h2 className="mb-1 mt-5 text-[11pt] uppercase tracking-wide">Last {d.payments.length} payments</h2>
      <table className="w-full border-collapse text-left">
        <tbody>{d.payments.map((p) => <tr key={p.order_key} className="border-b border-black/20"><td className="py-1">{p.date}</td><td>{p.method ?? ''}</td><td className="font-mono text-[9pt]">{p.order_key}</td><td className="text-right">{money(p.amount)}</td></tr>)}</tbody>
      </table>
    </article>
  )
}

/** A receipt letter, address label or member summary, printed from the browser. */
export function PrintPage() {
  const { id, what = '' } = useParams()
  const [sp] = useSearchParams()
  const orderKey = sp.get('order_key')
  const { data, isError, isLoading } = useQuery({
    queryKey: ['desk-print', id, what, orderKey],
    queryFn: () => getPrint(Number(id), what, orderKey),
    enabled: Number.isFinite(Number(id)) && ['receipt', 'label', 'summary'].includes(what),
    retry: false,
  })
  const printed = useRef(false)
  useEffect(() => {
    if (!data || printed.current) return
    printed.current = true
    const t = setTimeout(() => window.print(), 100)
    return () => { clearTimeout(t); printed.current = false }
  }, [data])
  if (isLoading) return <p className="p-6 text-paper/70">Getting it ready…</p>
  if (isError || !data) return <p role="alert" className="p-6">That cannot be printed. Check the order number and try again.</p>
  return (
    <div className="flex flex-col gap-4 p-6 print:p-0">
      <div className="no-print">
        <button type="button" onClick={() => window.print()} className="inline-flex min-h-[40px] items-center rounded-lg border border-ink-400 px-4 font-semibold text-paper hover:bg-ink-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow">Print again</button>
      </div>
      {data.what === 'receipt' && <Receipt d={data} />}
      {data.what === 'label' && <Label d={data} />}
      {data.what === 'summary' && <Summary d={data} />}
    </div>
  )
}
